import loaderClient, { clientNoLoader } from "api/reduxLoaderApi";
import { toast } from "react-toastify";
import {
  removeDashboard,
  setDashboards,
  setDashboardsLoading,
  setMenuDashboards,
  setWidgets,
  upsertMenuDashboard,
} from "./manageDashboardsSlice";

const DASHBOARDS_URL = "/v2/dashboards";
const DASHBOARD_WIDGETS_URL = "/v2/dashboard-widgets";
const WIDGETS_URL = "/v2/widgets";

export const getListMessage = (message) => {
  if (Array.isArray(message)) return message;
  if (Array.isArray(message?.results)) return message.results;
  if (Array.isArray(message?.data)) return message.data;
  return [];
};

const getErrorMessage = (res, fallbackMessage) =>
  typeof res?.message === "string" && res.message
    ? res.message
    : fallbackMessage;

const byOrder = (a, b) => (a.order ?? 0) - (b.order ?? 0);

// The table shows each dashboard's widgets from its dashboard-widget links;
// the same widget may appear more than once with a different title.
const normalizeDashboard = (dashboard, linksByDashboard) => {
  const widgets = [...(linksByDashboard[dashboard.id] || [])]
    .sort(byOrder)
    .map((link) => ({
      id: link.id,
      name: link.configuration?.title || link.widget?.name || "",
      visualization_type: link.widget?.visualization_type,
    }));
  return { ...dashboard, widgets };
};

/* -------------------------------------------------------------------------- */
export const getDashboardWidgetOptions = () => async (dispatch) => {
  const res = await loaderClient("get")(`${WIDGETS_URL}?limit=all`)({})({});
  if (res?.error) {
    toast.error(getErrorMessage(res, "Failed to load widgets"));
    return;
  }
  dispatch(setWidgets(getListMessage(res.message)));
};

export const getManagedDashboards = () => async (dispatch) => {
  dispatch(setDashboardsLoading(true));
  const [dashboardsRes, linksRes] = await Promise.all([
    loaderClient("get")(DASHBOARDS_URL)({})({}),
    loaderClient("get")(DASHBOARD_WIDGETS_URL)({})({}),
  ]);
  dispatch(setDashboardsLoading(false));
  if (dashboardsRes?.error) {
    toast.error(getErrorMessage(dashboardsRes, "Failed to load dashboards"));
    return;
  }
  const linksByDashboard = linksRes?.error
    ? {}
    : getListMessage(linksRes.message).reduce((acc, link) => {
        const dashboardId = link?.dashboard?.id || link?.dashboard;
        if (!dashboardId) return acc;
        (acc[dashboardId] = acc[dashboardId] || []).push(link);
        return acc;
      }, {});
  const dashboards = getListMessage(dashboardsRes.message).map((dashboard) =>
    normalizeDashboard(dashboard, linksByDashboard)
  );
  dispatch(setDashboards(dashboards));
};

// Header menu list: runs on every app load, so it skips the global loader and
// fails silently.
export const getMenuDashboards = () => async (dispatch) => {
  const res = await clientNoLoader("get")(DASHBOARDS_URL)({})({});
  if (res?.error) return;
  dispatch(setMenuDashboards(getListMessage(res.message)));
};

// Calls back with (error, messageOrDashboard); the created DTO carries the id
// the builder page needs.
export const createManagedDashboard =
  ({ name }, callback = undefined) =>
  async (dispatch) => {
    const res = await loaderClient("post")(DASHBOARDS_URL)({ name })({});
    if (res?.error) {
      callback?.(true, getErrorMessage(res, "Failed to create dashboard"));
      return;
    }
    dispatch(upsertMenuDashboard(res.message));
    callback?.(false, res.message);
  };

export const updateManagedDashboard =
  (dashboardId, { name }, callback = undefined) =>
  async (dispatch) => {
    const res = await loaderClient("patch")(`${DASHBOARDS_URL}/${dashboardId}`)(
      { name }
    )({});
    if (res?.error) {
      callback?.(true, getErrorMessage(res, "Failed to update dashboard"));
      return;
    }
    dispatch(upsertMenuDashboard(res.message));
    callback?.(false, res.message);
  };

export const deleteManagedDashboard =
  (dashboardId, callback = undefined) =>
  async (dispatch) => {
    const res = await loaderClient("delete")(
      `${DASHBOARDS_URL}/${dashboardId}`
    )({})({});
    if (res?.error) {
      callback?.(true, getErrorMessage(res, "Failed to delete dashboard"));
      return;
    }
    dispatch(removeDashboard(dashboardId));
    callback?.(false);
  };

/* ------------------------------ Builder page ------------------------------ */
// Resolves { dashboard, links } or { error, message }.
export const fetchDashboardBuilderData = async (dashboardId) => {
  const [dashboardRes, linksRes] = await Promise.all([
    loaderClient("get")(`${DASHBOARDS_URL}/${dashboardId}`)({})({}),
    loaderClient("get")(`${DASHBOARD_WIDGETS_URL}?dashboard=${dashboardId}`)(
      {}
    )({}),
  ]);
  if (dashboardRes?.error)
    return {
      error: true,
      message: getErrorMessage(dashboardRes, "Dashboard not found"),
    };
  if (linksRes?.error)
    return {
      error: true,
      message: getErrorMessage(linksRes, "Failed to load dashboard widgets"),
    };
  return {
    dashboard: dashboardRes.message,
    links: getListMessage(linksRes.message).sort(byOrder),
  };
};

// Replaces the dashboard's widgets with the builder state in one transaction:
// items with an id are updated, new ones created and omitted links deleted.
// Resolves { links } with the saved links, or { error, message }.
export const saveDashboardWidgets = async (dashboardId, items) => {
  const res = await loaderClient("put")(
    `${DASHBOARD_WIDGETS_URL}/dashboard/${dashboardId}`
  )({
    widgets: items.map((item, order) => ({
      ...(item.id && { dashboardWidgetId: item.id }),
      widget: item.widget.id,
      order,
      configuration: item.configuration,
    })),
  })({});
  if (res?.error)
    return {
      error: true,
      message: getErrorMessage(res, "Failed to save widgets"),
    };
  return { links: getListMessage(res.message).sort(byOrder) };
};
